import { AxiosError, AxiosRequestConfig } from "axios";
import api from "./api";

interface ErrorType {
  code: number,
  message: string
}

type Response<T = any> = {
  data?: T
  error?: ErrorType
}

type RequestSuccess<T> = { status: true, data: T }
type RequestFailure = { status: false, error: ErrorType }

class RequestError extends Error {
  constructor(error: ErrorType) {
    super()

    this.message = JSON.stringify(error)
  }
}

class Request<T = any, D = any> {
  static create<T = any, D = any>(options: AxiosRequestConfig<D>): Promise<RequestSuccess<T> | undefined> {
    return new Request<T, D>().request<T, D>(options, false) as Promise<RequestSuccess<T> | undefined>
  }

  static createWithServerError<T = any, D = any>(options: AxiosRequestConfig<D>): Promise<RequestSuccess<T> | RequestFailure | undefined> {
    return new Request<T, D>().request<T, D>(options, true)
  }

  private async request<T = any, D = any>(options: AxiosRequestConfig<D>, returnServerError = false): Promise<RequestSuccess<T> | RequestFailure | undefined> {
    try {
      const response = await api().request<Response<T>>(options)
      const { data, error } = response.data

      if (data !== undefined) {
        return {
          status: true as const,
          data: data
        }
      }
      else {
        if (returnServerError) return { status: false as const, error: error as ErrorType }
        throw new RequestError(error as ErrorType)
      }
    }
    catch (e) {
      const error = e as AxiosError<T, D>

      if (error.response) {
        console.error(new RequestError(error.response?.data as unknown as ErrorType))
      }
      else {
        console.error(e)
      }
    }
  }
}

export { Request }
